import React, { Component } from "react";
import { InjectedIntlProps } from "@ai360/core/intl";
import _ from "lodash";
import {
    Checkbox,
    DialogBox,
    DialogBoxFooterType,
    NoLink,
    NumericInput,
    ZeroToInfiniteGrid,
    SelectInput,
} from "~/core";
import { withEditableGrid } from "~/hocs";
import { messages } from "../../i18n-messages";
import { createAddLinkLabelText, createAddEditModalTitle } from "~/i18n-messages";
import { mapToPicklistValue, prepareSelectableOptions } from "~/admin/utils";
import { adminData } from "~/admin/data";
import { IComponentState, INutrientListProps } from "../interfaces";

const PROPS_NUTRIENT_GUID = "nutrientGuid";
const PROPS_NUTRIENT_TYPE = "nutrientName";
const PROPS_NUTRIENT_PERCENTAGE = "nutrientPercent";
const PROPS_NUTRIENT_IS_PRIMARY = "isPrimary";

interface INutrientListState extends IComponentState {
    lastOptions: any;
    lastRecord: any;
    records: Record<string, any>[];
}

class NutrientList extends Component<INutrientListProps & InjectedIntlProps, INutrientListState> {
    constructor(props) {
        super(props);
        this.state = {
            editOptions: [],
            editingItem: false,
            records: [],
            lastRecord: null,
            lastOptions: null,
        };
    }

    static getDerivedStateFromProps(
        nextProps: INutrientListProps & InjectedIntlProps,
        prevState: INutrientListState
    ): Partial<INutrientListState> | null {
        const { record, options } = nextProps;
        if (record === prevState.lastRecord && options === prevState.lastOptions) {
            return null;
        }
        const records = (record || []).map((recordItem) => {
            const selectedNutrientName = _.find(
                options,
                (nutrient) => nutrient[PROPS_NUTRIENT_GUID] === recordItem[PROPS_NUTRIENT_GUID]
            );
            return {
                ...recordItem,
                [PROPS_NUTRIENT_TYPE]:
                    (selectedNutrientName && selectedNutrientName[PROPS_NUTRIENT_TYPE]) ||
                    recordItem[PROPS_NUTRIENT_TYPE],
            };
        });
        const nextState: Partial<INutrientListState> = {
            records,
            lastRecord: record,
            lastOptions: options,
        };
        if (options !== prevState.lastOptions) {
            nextState.editOptions = prepareSelectableOptions(options, {
                guid: PROPS_NUTRIENT_GUID,
                label: PROPS_NUTRIENT_TYPE,
                id: "",
            });
        }
        return nextState;
    }

    onEdit = (record, index) => {
        this.setState({ editingItem: true });
        const keysToUpdate = [
            { name: [PROPS_NUTRIENT_TYPE], key: PROPS_NUTRIENT_TYPE },
            { name: [PROPS_NUTRIENT_GUID], key: PROPS_NUTRIENT_GUID },
            PROPS_NUTRIENT_PERCENTAGE,
        ];
        this.props.onEdit({ record, index, keysToUpdate });
    };

    onDelete = (record, index) => {
        this.props.onDelete({ record, index });
    };

    onAdd = () => {
        this.setState({ editingItem: false });
        this.props.onAdd();
    };

    renderAddEditModal = () => {
        const { formatMessage, isEditing, editData, onAddOrEditItem, onChange, toggleModal } =
            this.props;
        const formData = { ...editData };
        return (
            <DialogBox
                forceOverflow
                hideCloseX
                title={createAddEditModalTitle(
                    this.state.editingItem,
                    formatMessage,
                    messages.nutrient
                )}
                actionDisabled={!editData[PROPS_NUTRIENT_PERCENTAGE]}
                onClose={() => toggleModal("isEditing", false)}
                onAction={() => onAddOrEditItem()}
                isOpen={isEditing}
                footerType={DialogBoxFooterType.ACTION_CANCEL}
                action="save"
                testId="nutrient-list-add-edit-modal"
            >
                <SelectInput
                    tabIndex={0}
                    initialFilterStr=""
                    optionIsHiddenKey={adminData.PROPS_ACTIVE_YN}
                    options={this.state.editOptions}
                    placeholderText={formatMessage(messages.nutrient)}
                    value={mapToPicklistValue({
                        options: this.state.editOptions,
                        selectedGuid: formData[PROPS_NUTRIENT_GUID],
                    })}
                    onChange={({ guid, name }) => {
                        onChange({
                            [PROPS_NUTRIENT_GUID]: guid,
                            [PROPS_NUTRIENT_TYPE]: name,
                        });
                    }}
                    required
                    testId="nutrient-list-nutrient-select"
                />
                <NumericInput
                    tabIndex={0}
                    scale={4}
                    precision={7}
                    containerClassNames={["form-input-id"]}
                    placeholderText={formatMessage(messages.nutrientPercent)}
                    labelText={formatMessage(messages.nutrientPercent)}
                    value={formData[PROPS_NUTRIENT_PERCENTAGE] || ""}
                    onChange={(value) => onChange({ [PROPS_NUTRIENT_PERCENTAGE]: value })}
                    required
                    testId="nutrient-list-nutrient-percentage-input"
                />
            </DialogBox>
        );
    };

    printIsPrimary = (value, record) => {
        return (
            <Checkbox
                className="agvance-checkbox nutrient-primary-checkbox"
                value={record.isPrimary}
                onChange={(event, val) => {
                    event.stopPropagation();
                    if (val) {
                        const newNutrientList = this.props.record.map((n) => {
                            return {
                                ...n,
                                [PROPS_NUTRIENT_IS_PRIMARY]:
                                    n[PROPS_NUTRIENT_GUID] === record[PROPS_NUTRIENT_GUID] && val,
                            };
                        });
                        this.props.onTextChange(null, newNutrientList);
                    }
                }}
                testId={`nutrient-list-is-primary-checkbox-${record[PROPS_NUTRIENT_GUID]}`}
            />
        );
    };

    render() {
        const { formatMessage, renderDeleteModal } = this.props;
        const { records } = this.state;
        return (
            <div className={"form-section-child-stretch mini-grid"}>
                {records && records.length > 0 && (
                    <ZeroToInfiniteGrid
                        records={records}
                        columns={{
                            [PROPS_NUTRIENT_TYPE]: {
                                title: formatMessage(messages.nutrient),
                            },
                            [PROPS_NUTRIENT_PERCENTAGE]: {
                                title: formatMessage(messages.percentage),
                            },
                            [PROPS_NUTRIENT_IS_PRIMARY]: {
                                title: formatMessage(messages.primary),
                                printer: this.printIsPrimary,
                            },
                        }}
                        onEdit={this.onEdit}
                        onDelete={this.onDelete}
                    />
                )}
                {this.renderAddEditModal()}
                {renderDeleteModal()}
                <div className="add-link-container">
                    <NoLink
                        className="add-link"
                        label={createAddLinkLabelText(formatMessage, messages.nutrient)}
                        onClick={this.onAdd}
                    ></NoLink>
                </div>
            </div>
        );
    }
}

export default withEditableGrid(NutrientList);
